import { useCallback, useEffect, useRef } from 'react'
import { logger as createLogger } from '#utils/logger'
import type { CopyShortcutHandler } from './ShortcutContext'

const copyLogger = createLogger('📃 Copy Shortcut Context:')

export const useCopyShortcut = () => {
  const onCopyInvoked = useRef<CopyShortcutHandler>(undefined)
  const clearOnCopyInvoked = useCallback((context: string) => {
    if (onCopyInvoked.current && onCopyInvoked.current.context !== context) {
      copyLogger.warn(
        `Cleared copy context of '${onCopyInvoked.current.context}', while the caller wanted to clear '${context}', this is probably a user error.`,
      )
    }
    onCopyInvoked.current = undefined
  }, [])

  const setOnCopyInvoked = useCallback(
    (newHandler: CopyShortcutHandler) => {
      if (onCopyInvoked.current) {
        copyLogger.warn(
          `Already attached a copy context with context name '${onCopyInvoked.current.context}', replacing with '${newHandler?.context}'`,
        )
      }
      onCopyInvoked.current = newHandler
      return () => clearOnCopyInvoked(newHandler.context)
    },
    [clearOnCopyInvoked],
  )

  useEffect(() => {
    let isCopyHandlerDisabled = false
    const copyInvoke = async (e: KeyboardEvent) => {
      if (isCopyHandlerDisabled) {
        copyLogger.debug('Copy shortcut disabled')
        return
      }
      copyLogger.debug('Copy shortcut invoked')
      if (onCopyInvoked.current) {
        try {
          isCopyHandlerDisabled = true
          await onCopyInvoked.current.onCopy(onCopyInvoked.current.context, e)
        } finally {
          isCopyHandlerDisabled = false
        }
      }
    }

    const keyHandler = async (e: KeyboardEvent) => {
      if (e.code === 'KeyC' && (e.ctrlKey || e.metaKey) && onCopyInvoked.current) {
        copyLogger.debug('Copy shortcut key event')
        e.preventDefault()
        e.stopPropagation()
        void copyInvoke(e)
        return true
      }
    }
    copyLogger.debug('Copy handler registered')
    window.addEventListener('keydown', keyHandler)
    return () => {
      copyLogger.debug(`Global copy handler removed`)
      window.removeEventListener('keydown', keyHandler)
    }
  }, [])

  return {
    handler: onCopyInvoked,
    /**
     * Use to register a new handler when the copy shortcut is being invoked.
     * The handler returns a callback that can be used to unregister the handler.
     * @example
     * const { copy: { setOnCopyInvoked }} = useShortcutContext();
     * useEffect(() =>
     *  setOnCopyInvoked({
     *    context: "handler-context-name",
     *    onCopy: () => { ... }
     * }), [])
     */
    setOnCopyInvoked,
    clearOnCopyInvoked,
  }
}
